import type { ChatItem, ToolUseItem } from '../../../contracts/harness.types';
import { RUN_COMPUTER_USE_TOOL_NAME } from '../../../data/harness/timeline/derive-is-computer-use';

/**
 * Decide whether a tool name represents a desktop / computer-use tool.
 *
 * `desktop_` / `rpa_` / `computer*` cover the current Tauri RPA tools;
 * keep prefix-matching so future tools opt in by naming convention.
 * `run_computer_use_task` is the generic computer-use delegate tool name;
 * skill-named computer-use rows are identified via ``ToolUseItem.isComputerUse``.
 */
export const DESKTOP_TOOL_PREFIXES = ['desktop_', 'rpa_', 'computer'] as const;
export const DESKTOP_TOOL_NAMES = new Set<string>([RUN_COMPUTER_USE_TOOL_NAME]);

export const isDesktopTool = (toolName: string | undefined): boolean => {
  if (!toolName) return false;
  const lower = toolName.toLowerCase();
  if (DESKTOP_TOOL_NAMES.has(lower)) return true;
  return DESKTOP_TOOL_PREFIXES.some((p) => lower.startsWith(p));
};

export const BASH_TOOL_NAMES = [
  'list_directory',
  'read_file',
  'search_files',
  'stat_file',
  'create_file',
  'rename_file',
] as const;

export const isBashTool = (toolName: string | undefined): boolean =>
  !!toolName && (BASH_TOOL_NAMES as ReadonlyArray<string>).includes(toolName);

/**
 * Pull the still-running tool calls out of the harness timeline.
 *
 * The harness ``ToolUseItem`` already merges the tool call with its
 * result and carries a reconciled ``status``. "In flight" therefore
 * means the merge hasn't seen a terminal frame yet: ``running`` (a
 * tool-execution-start with no matching complete/error) or ``queued``
 * (a call received whose execution hasn't started yet — treated as still
 * open so chat/delegate consumers don't drop unresolved task UI). ``complete`` /
 * ``error`` / ``pending_approval`` are explicitly NOT in flight.
 */
export const findInFlightToolUses = (items: ChatItem[]): ToolUseItem[] =>
  items.filter(
    (item): item is ToolUseItem =>
      item.kind === 'tool_use' && (item.status === 'running' || item.status === 'queued')
  );
